import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type User } from '@avanta/shared/types/users';
import { getPatientName } from '@avanta/shared/utils/appointments';
import { IdCard, Mail, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { type Appointment } from 'types/appointments';
import { getActiveRecordCode, getLastSessionDisplay, getSickLeaveStartDate } from './utils';

const EMPTY_VALUE = '—';

interface PatientSummaryProps {
  patient: User;
  appointments: Appointment[];
}

interface SummaryMetaRowProps {
  label: string;
  value: string;
  emphasizeValue?: boolean;
}

const SummaryMetaRow = ({
  label,
  value,
  emphasizeValue = false,
}: SummaryMetaRowProps): React.ReactElement => (
  <div className="flex min-w-0 flex-wrap items-baseline gap-x-1 text-sm leading-4.5">
    <span className="text-teal-600 shrink-0">{label}</span>
    <span className={emphasizeValue ? 'text-denim-800 font-medium' : 'text-denim-800'}>
      {value}
    </span>
  </div>
);

export const PatientSummary = ({
  patient,
  appointments,
}: PatientSummaryProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { data: patientRecord } = useFetchActivePatientRecord(patient.id);

  const activeRecordCode = getActiveRecordCode(patientRecord) ?? EMPTY_VALUE;
  const sickLeaveStart = getSickLeaveStartDate(patientRecord) ?? EMPTY_VALUE;
  const lastSession = getLastSessionDisplay(appointments) ?? EMPTY_VALUE;

  return (
    <Card variant="primary" className="p-0 am-md:p-0">
      <div className="px-8 py-6">
        <h2 className="text-denim-800 text-xl leading-6 font-medium">{getPatientName(patient)}</h2>
        <div className="mt-4 flex flex-col gap-8 am-lg:flex-row am-lg:items-start am-lg:justify-between am-lg:gap-12">
          <div className="flex w-full max-w-md min-w-0 flex-col gap-3">
            <div className="flex min-w-0 items-center">
              <Mail className="text-teal-600 size-4 shrink-0" aria-hidden />
              <span className="text-denim-800 ml-2 truncate text-sm leading-4.5">
                {patient.email || EMPTY_VALUE}
              </span>
            </div>
            <div className="flex min-w-0 items-center">
              <IdCard className="text-teal-600 size-4 shrink-0" aria-hidden />
              <span className="text-denim-800 ml-2 truncate text-sm leading-4.5">
                {patient.nif || EMPTY_VALUE}
              </span>
            </div>
            <div className="flex min-w-0 items-center">
              <Phone className="text-teal-600 size-4 shrink-0" aria-hidden />
              <span className="text-denim-800 ml-2 truncate text-sm leading-4.5">
                {patient.contactPhone || EMPTY_VALUE}
              </span>
            </div>
          </div>
          <div className="flex w-full max-w-md min-w-0 flex-col gap-3">
            <SummaryMetaRow
              label={t('PatientSummary.activeRecord.label')}
              value={activeRecordCode}
              emphasizeValue={activeRecordCode !== EMPTY_VALUE}
            />
            <SummaryMetaRow
              label={t('PatientSummary.sickLeaveStart.label')}
              value={sickLeaveStart}
            />
            <SummaryMetaRow label={t('PatientSummary.lastSession.label')} value={lastSession} />
          </div>
        </div>
      </div>
    </Card>
  );
};
